import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { Observable, catchError, throwError } from 'rxjs';
import { EAPiErrorCode } from '../enums';
import { IApiError } from '../interfaces';
import { ToastMessagesService } from '../services/toast-messages.service';
import { AppConfigUtils } from '../utils';

/**
 * ApiErrorsInterceptor
 *
 * @export
 * @class ApiErrorsInterceptor
 * @typedef {ApiErrorsInterceptor}
 * @implements {HttpInterceptor}
 */
@Injectable()
export class ApiErrorsInterceptor implements HttpInterceptor {
  /**
   * Creates an instance of ApiErrorsInterceptor.
   *
   * @constructor
   * @param {AppConfigUtils} appConfigUtils
   * @param {ToastMessagesService} toastMessagesService
   */
  constructor(
    private appConfigUtils: AppConfigUtils,
    private toastMessagesService: ToastMessagesService,
    private translocoService: TranslocoService
  ) {}

  /**
   * Intercept http requests and add the Authorization header if the request is for the internal API
   *
   * @param {HttpRequest<any>} req
   * @param {HttpHandler} next
   * @returns {Observable<HttpEvent<any>>}
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.appConfigUtils.isApiUrlIn(req.url)) {
      return next.handle(req).pipe(
        catchError((response: HttpErrorResponse) => {
          const errorMessage = (response.error?.errors || [])
            .map((error: IApiError) => this.errorMessage(error))
            .join('\n');

          if (errorMessage) {
            this.toastMessagesService.show({
              title: errorMessage,
              type: 'error',
            });
          }

          return throwError(() => response);
        })
      );
    }
    return next.handle(req);
  }

  errorMessage(error: IApiError): string {
    if (error.code === EAPiErrorCode.GENERIC_ERROR) {
      return this.translocoService.translate(marker('ERRORS.GENERIC_API_ERROR'));
    }

    if (apiErrorCodeTranslation[error.code]) {
      return this.translocoService.translate(apiErrorCodeTranslation[error.code]);
    }

    // NOTE: we decided to not show message coming from backend because they are not user friendly,
    // if we need custom message we should use the error code and traslate it

    // const nestedMessage = this.extractNestedErrorMessage(error.message);
    // if (nestedMessage) {
    //   return nestedMessage;
    // }

    // if (this.isSimpleString(error.message)) {
    //   return error.message;
    // }

    return this.translocoService.translate(marker('ERRORS.GENERIC_API_ERROR'));
  }

  private isSimpleString(message: string): boolean {
    if (!message) return false;
    const trimmed = message.trim();
    return trimmed.length > 0 && !trimmed.startsWith('{') && !trimmed.startsWith('[');
  }

  /**
   * Extract a nested message example "TEST MESSAGE" in this format:
   * "error on: URL sconosciuto error message: 400 Bad Request: \"{\"type\":\"https://httpstatuses.io/400\",\"title\":\"Bad request\",\"status\":400,\"error\":\"testMessage\",\"errorMessage\":\"TEST MESSAGE\"}\""
   *
   * @param message
   * @returns
   */
  private extractNestedErrorMessage(message: string): string | null {
    if (!message) return null;
    const jsonMatch = message.match(/\{.*\}/s);
    if (!jsonMatch) return null;
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      return parsed.errorMessage ?? null;
    } catch {
      return null;
    }
  }
}

const apiErrorCodeTranslation = {
  [EAPiErrorCode.COS_NOT_SUPERVISIONABLE]: marker('ERRORS.COS_NOT_SUPERVISIONABLE'),
  [EAPiErrorCode.DELEGATION_ALREADY_EXISTS]: marker('ERRORS.DELEGATION_ALREADY_EXISTS'),
  [EAPiErrorCode.MAX_REQUEST_REACHED]: marker('ERRORS.MAX_REQUEST_REACHED'),
  [EAPiErrorCode.DEL_001]: marker('ERRORS.DELEGATION_ALREADY_EXISTS'),
  [EAPiErrorCode.INTERNAL_INCONSISTENT_STATE]: marker('ERRORS.PERMISSION_UPGRADE_IN_PROGRESS'),
  [EAPiErrorCode.SSO_NOT_ENABLED]: marker('ERRORS.SSO_NOT_ENABLED'),
};
